import * as React from 'react';

import { enumFacilitySortOption } from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  Col,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
  Row,
  SearchField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck, LucideX, LucideXCircle } from 'lucide-react';

import { useAddUsersToRoleMutation } from './_graphql/useAddUsersToRoleMutation';
import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';
import { useUsersQuery } from './_graphql/useUsersQuery';

import {
  $addUsersDialogOpen,
  $addUsersDialogStep,
  $addUsersFormData,
  $canProceedToFacilities,
  $canSave,
  $clearFacilitySelections,
  $clearUserSelections,
  $closeAddUsersDialog,
  $existingUserIds,
  $goToFacilitiesStep,
  $goToUsersStep,
  $isAddingUsers,
  $isSingleFacilityMode,
  $selectedFacilityCount,
  $selectedFacilityIds,
  $selectedUserCount,
  $selectedUserIds,
  $toggleFacilitySelection,
  $toggleUserSelection,
} from '~/modules/settings/_state/addUsersToRoleDialog.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

type StepIndicatorProps = {
  currentStep: 'users' | 'facilities';
};

const StepIndicator = ({ currentStep }: StepIndicatorProps) => {
  const t = useTranslations();

  return (
    <Row className="@StepIndicator items-center gap-3">
      <Row className="items-center gap-1.5">
        {currentStep === 'facilities' ? (
          <div className="flex size-[26px] items-center justify-center rounded bg-success p-[5px]">
            <LucideCheck className="size-4 text-abs-white" />
          </div>
        ) : (
          <div className="flex size-[26px] items-center justify-center rounded border border-success bg-success/10 px-1.5 py-0.5">
            <span className="text-sm font-medium text-success">1</span>
          </div>
        )}
        <span className="text-sm font-medium text-success">
          {t.teamAndPermissions.roles.addUsersDialog.steps.users}
        </span>
      </Row>

      <div className="h-px w-4 bg-neutral/20" />

      <Row className="items-center gap-1.5">
        <div
          className={cn(
            'flex size-[26px] items-center justify-center rounded px-1.5 py-0.5 text-sm font-medium',
            currentStep === 'facilities'
              ? 'border border-success bg-success/10 text-success'
              : 'border border-neutral/30 text-neutral/50',
          )}
        >
          2
        </div>
        <span
          className={cn(
            'text-sm font-medium',
            currentStep === 'facilities' ? 'text-success' : 'text-neutral/50',
          )}
        >
          {t.teamAndPermissions.roles.addUsersDialog.steps.facilities}
        </span>
      </Row>
    </Row>
  );
};

const UsersStep = () => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const selectedUserIds = useAtomValue($selectedUserIds);
  const selectedUserCount = useAtomValue($selectedUserCount);
  const existingIds = useAtomValue($existingUserIds);
  const toggleUserSelection = useSetAtom($toggleUserSelection);
  const clearUserSelections = useSetAtom($clearUserSelections);

  const { data: { users = [] } = {}, isLoading } = useUsersQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit: 1000,
    offset: 0,
  });

  const activeUsers = React.useMemo(
    () =>
      users.filter(
        (user) => user.status !== 'archived' && !existingIds.has(user.id),
      ),
    [users, existingIds],
  );

  const filteredUsers = React.useMemo(() => {
    if (!debouncedSearchTerm) return activeUsers;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return activeUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(lowerSearch) ||
        user.email.toLowerCase().includes(lowerSearch),
    );
  }, [activeUsers, debouncedSearchTerm]);

  return (
    <Col className="@UsersStep flex-1 overflow-hidden">
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={
          t.teamAndPermissions.roles.addUsersDialog.users.searchPlaceholder
        }
        autoFocus
      />

      <Row className="items-center justify-between pt-4 pb-0.5">
        <span className="text-sm font-semibold text-neutral/60 uppercase">
          {t.teamAndPermissions.roles.addUsersDialog.users.header} (
          {filteredUsers.length})
        </span>
        {selectedUserCount > 0 && (
          <Button
            variant="text"
            color="primary"
            size="small"
            className="h-auto py-0 text-sm"
            icon={<LucideXCircle className="size-4" />}
            onPress={clearUserSelections}
          >
            {t.teamAndPermissions.roles.addUsersDialog.actions.clear} (
            {selectedUserCount})
          </Button>
        )}
      </Row>

      <Col className="flex-1 gap-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-neutral/50">
            Loading...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-4 text-center text-sm text-neutral/50">
            No users found
          </div>
        ) : (
          filteredUsers.map((user) => (
            <Checkbox
              key={user.id}
              isSelected={selectedUserIds.has(user.id)}
              onChange={() => toggleUserSelection(user.id)}
              className="rounded py-[5px] hover:bg-neutral/5"
            >
              <Col>
                <span className="text-sm leading-[22px]">{user.name}</span>
                <span className="text-xs leading-normal text-neutral/60">
                  {user.email}
                </span>
              </Col>
            </Checkbox>
          ))
        )}
      </Col>
    </Col>
  );
};

const FacilitiesStep = () => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const selectedFacilityIds = useAtomValue($selectedFacilityIds);
  const selectedFacilityCount = useAtomValue($selectedFacilityCount);
  const toggleFacilitySelection = useSetAtom($toggleFacilitySelection);
  const clearFacilitySelections = useSetAtom($clearFacilitySelections);

  const { data: { facilities = [] } = {}, isLoading } = useFacilitiesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: [enumFacilitySortOption.NameAsc],
    limit: 1000,
    offset: 0,
  });

  const filteredFacilities = React.useMemo(() => {
    if (!debouncedSearchTerm) return facilities;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return facilities.filter(
      (facility) =>
        facility.name.toLowerCase().includes(lowerSearch) ||
        facility.address.toLowerCase().includes(lowerSearch),
    );
  }, [facilities, debouncedSearchTerm]);

  return (
    <Col className="@FacilitiesStep flex-1 overflow-hidden">
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={
          t.teamAndPermissions.roles.addUsersDialog.facilities.searchPlaceholder
        }
        autoFocus
      />

      <Row className="items-center justify-between pt-4 pb-0.5">
        <span className="text-sm font-semibold text-neutral/60 uppercase">
          {t.teamAndPermissions.roles.addUsersDialog.facilities.header} (
          {filteredFacilities.length})
        </span>
        {selectedFacilityCount > 0 && (
          <Button
            variant="text"
            color="primary"
            size="small"
            className="h-auto py-0 text-sm"
            icon={<LucideXCircle className="size-4" />}
            onPress={clearFacilitySelections}
          >
            {t.teamAndPermissions.roles.addUsersDialog.actions.clear} (
            {selectedFacilityCount})
          </Button>
        )}
      </Row>

      <Col className="flex-1 gap-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-neutral/50">
            Loading...
          </div>
        ) : filteredFacilities.length === 0 ? (
          <div className="py-4 text-center text-sm text-neutral/50">
            No facilities found
          </div>
        ) : (
          filteredFacilities.map((facility) => (
            <Checkbox
              key={facility.id}
              isSelected={selectedFacilityIds.has(facility.id)}
              onChange={() => toggleFacilitySelection(facility.id)}
              className="rounded py-[5px] hover:bg-neutral/5"
            >
              <Col>
                <span className="text-sm leading-[22px]">{facility.name}</span>
                <span className="text-xs leading-normal text-neutral/60">
                  {facility.address}
                </span>
              </Col>
            </Checkbox>
          ))
        )}
      </Col>
    </Col>
  );
};

export const AddUsersToRoleDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useAtom($addUsersDialogOpen);
  const formData = useAtomValue($addUsersFormData);
  const currentStep = useAtomValue($addUsersDialogStep);
  const selectedUserIds = useAtomValue($selectedUserIds);
  const selectedFacilityIds = useAtomValue($selectedFacilityIds);
  const canProceedToFacilities = useAtomValue($canProceedToFacilities);
  const canSave = useAtomValue($canSave);
  const [isAddingUsers, setIsAddingUsers] = useAtom($isAddingUsers);
  const isSingleFacilityMode = useAtomValue($isSingleFacilityMode);
  const selectedUserCount = useAtomValue($selectedUserCount);

  const closeDialog = useSetAtom($closeAddUsersDialog);
  const goToFacilitiesStep = useSetAtom($goToFacilitiesStep);
  const goToUsersStep = useSetAtom($goToUsersStep);

  const { mutateAsync: addUsersToRole } = useAddUsersToRoleMutation();

  const handleSave = React.useCallback(async () => {
    if (!canSave) return;

    setIsAddingUsers(true);

    try {
      const userIds = Array.from(selectedUserIds);
      const facilityIds = Array.from(selectedFacilityIds);

      let totalSuccessCount = 0;
      let totalFailedCount = 0;

      for (const facilityId of facilityIds) {
        const result = await addUsersToRole({
          roleId: formData.roleId,
          facilityId,
          userIds,
        });

        if (result?.coreRoleAddUsers) {
          totalSuccessCount += result.coreRoleAddUsers.successCount;
          totalFailedCount += result.coreRoleAddUsers.failedCount;
        }
      }

      if (totalFailedCount === 0) {
        toast.success(
          t.teamAndPermissions.roles.addUsersDialog.messages.success,
        );
      } else if (totalSuccessCount > 0) {
        toast.warning(
          t.teamAndPermissions.roles.addUsersDialog.messages.partialSuccess
            .replace('{successCount}', totalSuccessCount.toString())
            .replace(
              '{totalCount}',
              (totalSuccessCount + totalFailedCount).toString(),
            ),
        );
      } else {
        toast.error(t.teamAndPermissions.roles.addUsersDialog.messages.error);
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['settings.teamPermissions.roleUsers'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['settings.teamPermissions.facilityRoles'],
        }),
      ]);
      closeDialog();
    } catch (_error) {
      toast.error(t.teamAndPermissions.roles.addUsersDialog.messages.error);
    } finally {
      setIsAddingUsers(false);
    }
  }, [
    canSave,
    selectedUserIds,
    selectedFacilityIds,
    formData.roleId,
    addUsersToRole,
    queryClient,
    closeDialog,
    setIsAddingUsers,
    toast,
    t,
  ]);

  const title = t.teamAndPermissions.roles.addUsersDialog.title.replace(
    '{roleName}',
    formData.roleName,
  );

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="@AddUsersToRoleDialog"
    >
      <DialogContent
        className="h-[600px] max-h-[80vh] min-w-[500px]"
        closeButton={false}
      >
        {!isSingleFacilityMode && (
          <div className="flex flex-col rounded-t-lg bg-[#fbfbfc]">
            <div className="flex items-center justify-between px-6 pt-5 pb-1">
              <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
              <button
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center rounded bg-neutral/5 p-[3px]"
                aria-label="Close"
              >
                <LucideX className="size-6 text-neutral/60" />
              </button>
            </div>
            <div className="border-b border-neutral/10 px-6 pt-3 pb-4">
              <StepIndicator currentStep={currentStep} />
            </div>
          </div>
        )}

        <div className="flex flex-1 flex-col gap-5 overflow-hidden px-6 py-5">
          {isSingleFacilityMode || currentStep === 'users' ? (
            <UsersStep />
          ) : (
            <FacilitiesStep />
          )}
        </div>

        <DialogFooter>
          {isSingleFacilityMode ? (
            <>
              <Button
                variant="outline"
                color="neutral"
                onPress={closeDialog}
                isDisabled={isAddingUsers}
              >
                {t.teamAndPermissions.actions.discard}
              </Button>
              <Button
                variant="solid"
                color="primary"
                onPress={handleSave}
                isDisabled={!canSave || isAddingUsers}
                isLoading={isAddingUsers}
              >
                {t.teamAndPermissions.roles.addUsersDialog.actions.addUser.replace(
                  '{count}',
                  selectedUserCount.toString(),
                )}
              </Button>
            </>
          ) : currentStep === 'users' ? (
            <Button
              aria-label={
                t.teamAndPermissions.roles.addUsersDialog.actions.next
              }
              variant="solid"
              color="primary"
              onPress={goToFacilitiesStep}
              isDisabled={!canProceedToFacilities}
            >
              {t.teamAndPermissions.roles.addUsersDialog.actions.next}
            </Button>
          ) : (
            <>
              <Button
                aria-label={
                  t.teamAndPermissions.roles.addUsersDialog.actions.back
                }
                variant="outline"
                color="neutral"
                onPress={goToUsersStep}
                isDisabled={isAddingUsers}
              >
                {t.teamAndPermissions.roles.addUsersDialog.actions.back}
              </Button>
              <Button
                aria-label={
                  t.teamAndPermissions.roles.addUsersDialog.actions.save
                }
                variant="solid"
                color="primary"
                onPress={handleSave}
                isDisabled={!canSave || isAddingUsers}
                isLoading={isAddingUsers}
              >
                {t.teamAndPermissions.roles.addUsersDialog.actions.save}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
